import { useParams, Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge, getStatusDisplay, normalizeStatus } from "@/components/ui/status-badge";
import { RichTextDisplay } from "@/components/ui/rich-text-display";
import { ArrowLeft, FileText, Calendar, Users, User, CheckCircle, Clock, Edit, Trash2, Eye, Save, Send, X, XCircle, Wrench, Sparkles, Loader2, MoreHorizontal, ChevronDown, ShieldCheck, Plus, ListChecks } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { format } from "date-fns";
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { WorkflowVisualization } from "@/components/policy/WorkflowVisualization";
import { PolicyActivityLog } from "@/components/policy/PolicyActivityLog";
import ComplianceChecklist from "@/components/policy/ComplianceChecklist";
import ExecutiveSummary from "@/components/policy/ExecutiveSummary";
import { PolicyComments } from "@/components/policy/PolicyComments";
import { ApprovalConditions } from "@/components/policy/ApprovalConditions";
import { CommentAnnotator } from "@/components/policy/CommentAnnotator";
import PolicyPreview from "@/components/policy/PolicyPreview";
import { TranslationButton } from "@/components/policy/TranslationButton";
import { ActivityLogger } from "@/lib/activity-logger";
import logo from "@/assets/logo.png";
import type { TextSelection } from "@/lib/comment-utils";
import { CreateTemplateModal } from "@/components/templates/CreateTemplateModal";
import { PolicyCover } from "@/components/policy/PolicyCover";
import { convertPolicyToTemplate } from "@/lib/template-utils";
import { isPureViewer, canEditPolicies } from "@/lib/viewer-utils";
import { Skeleton } from "@/components/ui/skeleton";
import { generatePolicyQuiz } from "@/lib/quiz-generator";
import { TaskFormModal } from "@/components/compliance/TaskFormModal";
import { formatFramework } from "@/hooks/use-compliance-programs";
import type { ComplianceTask } from "@/hooks/use-compliance-tasks";

interface PolicyData {
  id: string;
  title: string;
  department: string | null;
  content: string;
  version: number;
  status: 'draft' | 'under_review' | 'pending_approval' | 'approved' | 'rejected';
  created_by: string;
  created_at: string;
  updated_at: string;
  effective_date: string | null;
  next_renewal_date: string | null;
  assigned_reviewers: string[] | null;
  assigned_approvers: string[] | null;
  creator: {
    name: string;
    designation?: string;
  } | null;
  program_id: string | null;
  program?: {
    id: string;
    name: string;
    framework: string;
  } | null;
}

const PolicyView = () => {
  const { id } = useParams<{ id: string }>();
  const [policy, setPolicy] = useState<PolicyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [reviewers, setReviewers] = useState<any[]>([]);
  const [approvers, setApprovers] = useState<any[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [activityLog, setActivityLog] = useState<any[]>([]);
  const [linkedControls, setLinkedControls] = useState<any[]>([]);
  const [linkedTasks, setLinkedTasks] = useState<ComplianceTask[]>([]);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isCreateTemplateOpen, setIsCreateTemplateOpen] = useState(false);
  const [isReopenDialogOpen, setIsReopenDialogOpen] = useState(false);
  const [isActionDialogOpen, setIsActionDialogOpen] = useState(false);
  const [isCreatingQuiz, setIsCreatingQuiz] = useState(false);
  const [isAuditTrailOpen, setIsAuditTrailOpen] = useState(false);
  const [sidebarTab, setSidebarTab] = useState('workflow');
  const [pendingSelection, setPendingSelection] = useState<import('@/lib/comment-utils').TextSelection | null>(null);
  const [actionType, setActionType] = useState<'submit' | 'send_approval' | 'approve' | 'reject' | 'rework' | null>(null);
  const [actionComments, setActionComments] = useState('');
  const { profile, roles, hasAnyRole, company } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Check if user can edit policies
  const canEdit = canEditPolicies(roles);
  const isViewer = isPureViewer(roles);
  
  // Permission checks
  const isCreator = policy?.created_by === profile?.id;
  const isAdmin = hasAnyRole(['admin']);
  const canEditPolicy = isCreator || isAdmin;
  const isAssignedReviewer = policy?.assigned_reviewers?.includes(profile?.id || '') || false;
  const isAssignedApprover = policy?.assigned_approvers?.includes(profile?.id || '') || false;

  const canCreateQuiz =
    policy?.status === "approved" &&
    (isCreator || isAssignedReviewer || isAssignedApprover || isAdmin);
  
  // Check if PDF preview is enabled for the company
  const allowPdfPreview = company?.allow_pdf_preview !== false; // Default to true if not set

  useEffect(() => {
    if (id) {
      fetchPolicy();
    }
  }, [id, profile?.company_id]);

  // Fetch activity log when policy is loaded
  useEffect(() => {
    if (policy?.id) {
      fetchActivityLog();
    }
  }, [policy?.id]);

  const fetchActivityLog = async () => {
    if (!policy?.id || !profile?.company_id) return;
    
    try {
      const { data, error } = await supabase
        .from('activity_logs')
        .select(`
          id,
          action_type,
          resource_title,
          details,
          created_at,
          user_id,
          performed_by:profiles!activity_logs_user_id_fkey(name)
        `)
        .eq('company_id', profile.company_id)
        .eq('resource_id', policy.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      const formattedLog = (data || []).map((item: any) => ({
        id: item.id,
        action_type: item.action_type,
        details: item.details,
        created_at: item.created_at,
        performed_by_name: item.performed_by?.name || 'System'
      }));

      setActivityLog(formattedLog);
    } catch (error) {
      console.error('Error fetching activity log:', error);
      setActivityLog([]);
    }
  };

  const fetchPolicy = async () => {
    if (!profile?.company_id || !id) return;
    
    try {
      // Fetch the policy
      const { data: policyData, error: policyError } = await (supabase as any)
        .from('policies')
        .select('*, program:programs(id,name,framework)')
        .eq('id', id)
        .eq('company_id', profile.company_id)
        .maybeSingle();

      if (policyError) throw policyError;

      if (!policyData) {
        setPolicy(null);
        return;
      }

      // Fetch creator profile
      const { data: creatorData } = await supabase
        .from('profiles')
        .select('name, designation')
        .eq('id', policyData.created_by)
        .maybeSingle();

      const policyWithCreator = {
        ...policyData,
        creator: creatorData
      };

      setPolicy(policyWithCreator);

      const { data: controlData } = await (supabase as any)
        .from('policy_control_mappings')
        .select('id, program_id, framework_control:framework_controls(framework, control_id, title, domain)')
        .eq('policy_id', id)
        .eq('company_id', profile.company_id);

      setLinkedControls(controlData || []);

      const { data: taskData } = await (supabase as any)
        .from('compliance_tasks')
        .select(`
          *,
          primary_assignee:profiles!compliance_tasks_primary_assignee_id_fkey(id, name, email),
          program:programs!compliance_tasks_program_id_fkey(id, name, framework),
          control:framework_controls!compliance_tasks_control_id_fkey(id, control_id, title)
        `)
        .eq('policy_id', id)
        .eq('company_id', profile.company_id)
        .order('created_at', { ascending: false });

      setLinkedTasks(taskData || []);

      // Fetch reviewers and approvers if they exist
      const reviewerIds = policyData.assigned_reviewers || [];
      const approverIds = policyData.assigned_approvers || [];

      if (reviewerIds.length > 0) {
        const { data: reviewersData } = await supabase
          .from('profiles')
          .select('id, name, designation')
          .in('id', reviewerIds);
        setReviewers(reviewersData || []);
      }

      if (approverIds.length > 0) {
        const { data: approversData } = await supabase
          .from('profiles')
          .select('id, name, designation')
          .in('id', approverIds);
        setApprovers(approversData || []);
      }
    } catch (error) {
      console.error('Error fetching policy:', error);
      toast({
        title: "Error",
        description: "Failed to fetch policy",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (selection: TextSelection, commentContent: string) => {
    if (!profile || !policy) return;

    try {
      const { error } = await (supabase
        .from('policy_comments' as any)
        .insert({
          policy_id: policy.id,
          company_id: profile.company_id,
          created_by: profile.id,
          content: commentContent,
          comment_type: 'general',
          selected_text: selection.text,
          selection_start: selection.startIndex,
          selection_end: selection.endIndex,
          selection_context: selection.context,
        } as any));

      if (error) throw error;

      toast({
        title: "Success",
        description: "Comment added successfully",
      });
    } catch (error) {
      console.error('Error adding comment:', error);
      toast({
        title: "Error",
        description: "Failed to add comment",
        variant: "destructive",
      });
    }
  };

  // Refresh comments when a new one is added
  const handleCommentAdded = () => {
    // The PolicyComments component will refresh automatically
    // This is here in case we need to trigger a refresh from parent
  };

  const handleBackToReworkFromComment = async (sourceCommentId: string, reason?: string) => {
    await sendPolicyBackToRework(reason || "Sent back to rework from a revision request thread", sourceCommentId);
    await fetchPolicy();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/policy-library">
            <Button variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Library
            </Button>
          </Link>
        </div>

        {/* Policy Header Skeleton */}
        <Card className="shadow-card">
          <CardHeader>
            <div className="flex items-start gap-6">
              <Skeleton variant="shimmer" className="w-16 h-16 rounded" />
              <div className="flex-1 space-y-3">
                <Skeleton variant="shimmer" className="h-8 w-3/4" />
                <div className="flex items-center gap-4">
                  <Skeleton variant="shimmer" className="h-4 w-24" />
                  <Skeleton variant="shimmer" className="h-4 w-16" />
                </div>
              </div>
              <Skeleton variant="shimmer" className="h-8 w-20" />
            </div>
          </CardHeader>
        </Card>

        {/* Policy Info Skeleton */}
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle>Policy Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="space-y-1">
                  <Skeleton variant="shimmer" className="h-3 w-20" />
                  <Skeleton variant="shimmer" className="h-5 w-16" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Content Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-10 gap-6">
          <div className="md:col-span-2 lg:col-span-6 space-y-6">
            <Card className="shadow-card">
              <CardHeader>
                <Skeleton variant="shimmer" className="h-6 w-32" />
              </CardHeader>
              <CardContent>
                <Skeleton variant="shimmer" className="h-4 w-full mb-2" />
                <Skeleton variant="shimmer" className="h-4 w-full mb-2" />
                <Skeleton variant="shimmer" className="h-4 w-5/6 mb-2" />
                <Skeleton variant="shimmer" className="h-4 w-full mb-2" />
                <Skeleton variant="shimmer" className="h-4 w-4/6" />
              </CardContent>
            </Card>
          </div>
          <div className="space-y-6">
            <Card className="shadow-card">
              <CardHeader>
                <Skeleton variant="shimmer" className="h-6 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton variant="shimmer" className="h-20 w-full" />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (!policy) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/policy-library">
            <Button variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Library
            </Button>
          </Link>
        </div>
        <div className="text-center py-12">
          <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold text-muted-foreground mb-2">Policy not found</h3>
          <p className="text-sm text-muted-foreground">The requested policy could not be found.</p>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-success text-success-foreground';
      case 'under_review': return 'bg-warning text-warning-foreground';
      case 'draft': return 'bg-muted text-muted-foreground';
      case 'rejected': return 'bg-destructive text-destructive-foreground';
      case 'pending_approval': return 'bg-blue-100 text-blue-800';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'approved': return 'Approved';
      case 'under_review': return 'Under Review';
      case 'draft': return 'Draft';
      case 'rejected': return 'Rejected';
      case 'pending_approval': return 'Pending Approval';
      default: return status;
    }
  };

  const getDepartmentDisplay = (department: string) => {
    const departmentMap: Record<string, string> = {
      'data-privacy': 'Data Privacy',
      'security': 'Security',
      'hr': 'Human Resources', 
      'finance': 'Finance',
      'operations': 'Operations'
    };
    return departmentMap[department] || department;
  };

  const handleDeletePolicy = async () => {
    if (!policy?.id || !profile?.company_id) return;
    
    // Check if user has permission to delete
    if (!hasAnyRole(['creator', 'admin']) && policy.created_by !== profile?.id) {
      toast({
        title: "Permission Denied",
        description: "You don't have permission to delete this policy.",
        variant: "destructive",
      });
      return;
    }

    setDeleting(true);
    try {
      console.log('Starting policy deletion process for policy:', policy.id);
      
      // First, delete any related workflow entries
      const { data: workflowData, error: workflowError } = await supabase
        .from('policy_workflow')
        .delete()
        .eq('policy_id', policy.id)
        .eq('company_id', profile.company_id);

      if (workflowError) {
        console.error('Error deleting workflow entries:', workflowError);
        throw workflowError;
      }
      
      console.log('Workflow entries deleted:', workflowData);

      // Then delete the policy - include .select() to get the deleted data
      const { data: policyData, error: policyError } = await supabase
        .from('policies')
        .delete()
        .eq('id', policy.id)
        .eq('company_id', profile.company_id)
        .select();

      if (policyError) {
        console.error('Error deleting policy:', policyError);
        throw policyError;
      }
      
      console.log('Policy deletion response:', policyData);
      
      // Check if any rows were actually deleted
      if (!policyData || policyData.length === 0) {
        console.warn('No rows were deleted - policy might not exist or access denied');
        throw new Error('Policy could not be deleted. It may have already been removed or you may not have permission.');
      }

      // Log the deletion activity
      await ActivityLogger.logPolicyDeleted(
        profile.company_id,
        profile.id,
        policy.id,
        policy.title
      );

      toast({
        title: "Success",
        description: "Policy deleted successfully.",
      });

      // Navigate back to policy library with a refresh signal
      navigate('/policy-library', { state: { refresh: true } });
    } catch (error: any) {
      console.error('Error deleting policy:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete policy.",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleCreateTemplate = async (templateData: {
    title: string;
    description: string;
    category: string;
    department: string;
    tags: string[];
  }) => {
    if (!policy || !profile?.company_id || !profile?.id) return;

    try {
      await convertPolicyToTemplate(
        policy.id,
        profile.company_id,
        profile.id,
        templateData
      );

      toast({
        title: "Success",
        description: "Policy saved as internal template",
      });
    } catch (error) {
      console.error("Error creating template:", error);
      throw error;
    }
  };

  const handleSavePolicyTask = async (taskData: Partial<ComplianceTask>) => {
    if (!policy || !profile?.company_id || !profile?.id) return;

    const { error } = await (supabase as any)
      .from('compliance_tasks')
      .insert({
        ...taskData,
        policy_id: policy.id,
        program_id: taskData.program_id || policy.program_id || null,
        program_phase: taskData.program_phase || (taskData.program_id || policy.program_id ? 'policies' : null),
        reference_type: taskData.reference_type || (taskData.program_id || policy.program_id ? 'program' : 'policy'),
        company_id: profile.company_id,
        created_by: profile.id,
      });

    if (error) throw error;

    toast({
      title: "Task added",
      description: "The compliance task is linked to this policy.",
    });

    await fetchPolicy();
  };

  // Workflow action handlers
  const handleSubmitForReview = async () => {
    if (!policy || !profile?.company_id || !profile?.id) return;
    
    try {
      // Update policy status
      const { error: policyError } = await supabase
        .from('policies')
        .update({ status: 'under_review' })
        .eq('id', policy.id);
      
      if (policyError) throw policyError;
      
      // Create workflow entry
      const { error: workflowError } = await supabase
        .from('policy_workflow')
        .insert({
          policy_id: policy.id,
          status: 'under_review',
          assigned_to: policy.assigned_reviewers?.[0] || null,
          action: 'submitted',
          previous_status: policy.status,
          new_status: 'under_review',
          created_by: profile.id,
          company_id: profile.company_id,
          comments: actionComments || null
        });
      
      if (workflowError) throw workflowError;
      
      // Log activity
      await ActivityLogger.logPolicyStatusChanged(
        profile.company_id,
        profile.id,
        policy.id,
        policy.title,
        policy.status,
        'under_review'
      );
      
      // Add comment if provided
      if (actionComments) {
        const { error: commentError } = await supabase
          .from('policy_comments')
          .insert({
            policy_id: policy.id,
            company_id: profile.company_id,
            created_by: profile.id,
            content: `**Workflow Action: Submitted for Review**\n\n${actionComments}`,
            comment_type: 'general'
          });
        
        if (commentError) console.error('Error adding comment:', commentError);
      }
      
      toast({
        title: "Success",
        description: "Policy submitted for review!",
      });
      
      setIsActionDialogOpen(false);
      setActionComments('');
      fetchPolicy();
    } catch (error: any) {
      console.error('Error submitting policy:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to submit policy for review.",
        variant: "destructive",
      });
    }
  };
  
  const handleSendForApproval = async () => {
    if (!policy || !profile?.company_id || !profile?.id) return;
    
    try {
      // Update policy status
      const { error: policyError } = await supabase
        .from('policies')
        .update({ status: 'pending_approval' })
        .eq('id', policy.id);
      
      if (policyError) throw policyError;
      
      // Create workflow entry
      const { error: workflowError } = await supabase
        .from('policy_workflow')
        .insert({
          policy_id: policy.id,
          status: 'pending_approval',
          assigned_to: policy.assigned_approvers?.[0] || null,
          action: 'reviewed',
          previous_status: policy.status,
          new_status: 'pending_approval',
          created_by: profile.id,
          company_id: profile.company_id,
          comments: actionComments || null
        });
      
      if (workflowError) throw workflowError;
      
      // Log activity
      await ActivityLogger.logPolicyStatusChanged(
        profile.company_id,
        profile.id,
        policy.id,
        policy.title,
        policy.status,
        'pending_approval'
      );
      
      // Add comment if provided
      if (actionComments) {
        const { error: commentError } = await supabase
          .from('policy_comments')
          .insert({
            policy_id: policy.id,
            company_id: profile.company_id,
            created_by: profile.id,
            content: `**Workflow Action: Sent for Approval**\n\n${actionComments}`,
            comment_type: 'general'
          });
        
        if (commentError) console.error('Error adding comment:', commentError);
      }
      
      toast({
        title: "Success",
        description: "Policy sent for approval!",
      });
      
      setIsActionDialogOpen(false);
      setActionComments('');
      fetchPolicy();
    } catch (error: any) {
      console.error('Error sending for approval:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send policy for approval.",
        variant: "destructive",
      });
    }
  };
  
  const handleApprovePolicy = async () => {
    if (!policy || !profile?.company_id || !profile?.id) return;
    
    try {
      const { data: approvalResult, error: approvalError } = await supabase.rpc(
        'approve_policy_with_revision_guard',
        {
          _policy_id: policy.id,
          _actor_id: profile.id,
          _comments: actionComments || null,
        }
      );

      if (approvalError) throw approvalError;

      if (!approvalResult?.success) {
        const blockedCount = approvalResult?.open_revision_requests;
        const message = typeof blockedCount === "number"
          ? `Resolve ${blockedCount} open revision request${blockedCount === 1 ? "" : "s"} before approval.`
          : approvalResult?.message || "Approval is blocked.";
        toast({
          title: "Approval blocked",
          description: message,
          variant: "destructive",
        });
        return;
      }
      
      // Add comment if provided
      if (actionComments) {
        const { error: commentError } = await supabase
          .from('policy_comments')
          .insert({
            policy_id: policy.id,
            company_id: profile.company_id,
            created_by: profile.id,
            content: `**Workflow Action: Policy Approved**\n\n${actionComments}`,
            comment_type: 'general'
          });
        
        if (commentError) console.error('Error adding comment:', commentError);
      }
      
      toast({
        title: "Success",
        description: "Policy approved successfully!",
      });
      
      setIsActionDialogOpen(false);
      setActionComments('');
      fetchPolicy();
    } catch (error: any) {
      console.error('Error approving policy:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to approve policy.",
        variant: "destructive",
      });
    }
  };
  
  const handleRejectPolicy = async () => {
    if (!actionComments.trim()) {
      toast({
        title: "Validation Error",
        description: "Please provide a reason for rejection.",
        variant: "destructive",
      });
      return;
    }
    
    if (!policy || !profile?.company_id || !profile?.id) return;
    
    try {
      // Update policy status
      const { error: policyError } = await supabase
        .from('policies')
        .update({ status: 'rejected' })
        .eq('id', policy.id);
      
      if (policyError) throw policyError;
      
      // Create workflow entry
      const { error: workflowError } = await supabase
        .from('policy_workflow')
        .insert({
          policy_id: policy.id,
          status: 'rejected',
          assigned_to: profile.id,
          action: 'rejected',
          previous_status: policy.status,
          new_status: 'rejected',
          created_by: profile.id,
          company_id: profile.company_id,
          comments: actionComments
        });
      
      if (workflowError) throw workflowError;
      
      // Log activity
      await ActivityLogger.logPolicyStatusChanged(
        profile.company_id,
        profile.id,
        policy.id,
        policy.title,
        policy.status,
        'rejected'
      );
      
      // Add comment with rejection reason
      const { error: commentError } = await supabase
        .from('policy_comments')
        .insert({
          policy_id: policy.id,
          company_id: profile.company_id,
          created_by: profile.id,
          content: `**Workflow Action: Policy Rejected**\n\n${actionComments}`,
          comment_type: 'general'
        });
      
      if (commentError) console.error('Error adding comment:', commentError);
      
      toast({
        title: "Success",
        description: "Policy rejected.",
      });
      
      setIsActionDialogOpen(false);
      setActionComments('');
      fetchPolicy();
    } catch (error: any) {
      console.error('Error rejecting policy:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to reject policy.",
        variant: "destructive",
      });
    }
  };

  const sendPolicyBackToRework = async (reason?: string, sourceCommentId?: string) => {
    if (!policy || !profile?.company_id || !profile?.id) return false;

    const { data, error } = await supabase.rpc("send_policy_back_to_rework", {
      _policy_id: policy.id,
      _actor_id: profile.id,
      _reason: reason || null,
      _source_comment_id: sourceCommentId || null,
    });

    if (error) throw error;
    if (!data?.success) {
      throw new Error(data?.message || "Failed to send policy back to rework.");
    }

    return true;
  };

  const handleSendBackToRework = async () => {
    if (!policy || !profile?.company_id || !profile?.id) return;

    try {
      await sendPolicyBackToRework(actionComments || undefined);

      toast({
        title: "Policy moved to draft",
        description: "The policy has been sent back to rework.",
      });

      setIsActionDialogOpen(false);
      setActionComments("");
      fetchPolicy();
    } catch (error: any) {
      console.error("Error sending policy back to rework:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to send policy back to rework.",
        variant: "destructive",
      });
    }
  };

  const handleReopenAsDraft = async () => {
    if (!policy || !profile?.company_id || !profile?.id) return;
    try {
      const previousStatus = policy.status;
      // Delete existing embeddings/chunks for this policy to keep RAG consistent
      const { error: embeddingsError } = await supabase
        .from('policy_embeddings')
        .delete()
        .eq('policy_id', policy.id)
        .eq('company_id', profile.company_id);
      if (embeddingsError) throw embeddingsError;
      // Update policy status to draft
      const { error: policyError } = await supabase
        .from('policies')
        .update({ status: 'draft' })
        .eq('id', policy.id);
      if (policyError) throw policyError;

      // Add workflow entry
      const { error: workflowError } = await supabase
        .from('policy_workflow')
        .insert({
          policy_id: policy.id,
          status: 'draft',
          assigned_to: profile.id,
          action: 'reopened_for_edit',
          previous_status: previousStatus,
          new_status: 'draft',
          created_by: profile.id,
          company_id: profile.company_id,
          comments: 'Policy reopened for editing by user'
        });
      if (workflowError) throw workflowError;

      // Log activity
      await ActivityLogger.logPolicyStatusChanged(
        profile.company_id,
        profile.id,
        policy.id,
        policy.title,
        previousStatus,
        'draft'
      );

      toast({
        title: 'Policy moved to Drafts',
        description: 'You can now edit the policy. It will need review and approval again.',
      });

      setIsReopenDialogOpen(false);
      navigate(`/policy-creator?edit=${policy.id}`);
    } catch (error: any) {
      console.error('Error reopening as draft:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to move policy to Drafts.',
        variant: 'destructive',
      });
    }
  };
  
  const handleWorkflowAction = () => {
    if (!actionType) return;
    
    switch (actionType) {
      case 'submit':
        handleSubmitForReview();
        break;
      case 'send_approval':
        handleSendForApproval();
        break;
      case 'approve':
        handleApprovePolicy();
        break;
      case 'reject':
        handleRejectPolicy();
        break;
      case 'rework':
        handleSendBackToRework();
        break;
    }
  };

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <Link to="/policy-library">
          <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Back to Library
          </Button>
        </Link>
        <div className="flex items-center gap-2">
          {/* Secondary actions in overflow menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {allowPdfPreview && (
                <DropdownMenuItem onClick={() => setIsPreviewOpen(true)}>
                  <Eye className="w-4 h-4 mr-2" />
                  Preview
                </DropdownMenuItem>
              )}
              {(canEditPolicy || hasAnyRole(['creator', 'admin'])) && (
                <DropdownMenuItem onClick={() => setIsCreateTemplateOpen(true)}>
                  <Save className="w-4 h-4 mr-2" />
                  Save as Template
                </DropdownMenuItem>
              )}
              {policy && canCreateQuiz && profile?.company_id && profile?.id && (
                <DropdownMenuItem
                  disabled={isCreatingQuiz}
                  onClick={async () => {
                    setIsCreatingQuiz(true);
                    try {
                      const quiz = await generatePolicyQuiz({
                        policyTitle: policy.title,
                        policyContent: policy.content,
                        companyName: company?.name ?? undefined,
                      });
                      const { data: inserted, error } = await supabase
                        .from("policy_quizzes")
                        .insert({
                          company_id: profile.company_id,
                          policy_id: policy.id,
                          title: quiz.title,
                          description: quiz.description,
                          questions: quiz.questions,
                          created_by: profile.id,
                        })
                        .select("id")
                        .single();
                      if (error) throw error;
                      if (!inserted?.id) throw new Error("Failed to create quiz");
                      toast({ title: "Quiz created", description: "Redirecting to quiz editor…" });
                      navigate(`/quiz/${inserted.id}/edit`);
                    } catch (error: any) {
                      console.error("Error generating quiz:", error);
                      toast({
                        title: "Quiz generation failed",
                        description: error?.message || "We couldn't generate the quiz. Check your AI settings and try again.",
                        variant: "destructive",
                      });
                    } finally {
                      setIsCreatingQuiz(false);
                    }
                  }}
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Create Quiz
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Translate - standalone since it's its own dropdown */}
          {policy && (policy.status === 'approved' || canEditPolicy) && profile?.company_id && profile?.id && (
            <TranslationButton
              policyId={policy.id}
              title={policy.title}
              content={policy.content}
              department={policy.department}
              version={policy.version}
              effectiveDate={policy.effective_date}
              nextRenewalDate={policy.next_renewal_date}
              assignedReviewers={policy.assigned_reviewers}
              assignedApprovers={policy.assigned_approvers}
              companyId={profile.company_id}
              userId={profile.id}
            />
          )}

          {/* Edit Policy */}
          {canEditPolicy && (
            policy?.status === 'approved' ? (
              <Button variant="outline" size="sm" onClick={() => setIsReopenDialogOpen(true)}>
                <Edit className="w-4 h-4 mr-1.5" />
                Edit Policy
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={() => navigate(`/policy-creator?edit=${policy.id}`)}>
                <Edit className="w-4 h-4 mr-1.5" />
                Edit Policy
              </Button>
            )
          )}

          {/* Primary workflow actions */}
          {policy.status === 'draft' && canEditPolicy && (
            <Button size="sm" onClick={() => { setActionType('submit'); setIsActionDialogOpen(true); }}>
              <Send className="w-4 h-4 mr-1.5" />
              Submit for Review
            </Button>
          )}
          {policy.status === 'under_review' && (isAssignedReviewer || isAdmin) && (
            <Button size="sm" onClick={() => { setActionType('send_approval'); setIsActionDialogOpen(true); }}>
              <Send className="w-4 h-4 mr-1.5" />
              Send for Approval
            </Button>
          )}
          {['under_review', 'pending_approval'].includes(policy.status) && (isAssignedReviewer || isAssignedApprover || isAdmin) && (
            <Button variant="outline" size="sm" onClick={() => { setActionType('rework'); setIsActionDialogOpen(true); }}>
              <Wrench className="w-4 h-4 mr-1.5" />
              Back to Rework
            </Button>
          )}
          {policy.status === 'pending_approval' && (isAssignedApprover || isAdmin) && (
            <>
              <Button size="sm" onClick={() => { setActionType('approve'); setIsActionDialogOpen(true); }}>
                <CheckCircle className="w-4 h-4 mr-1.5" />
                Approve
              </Button>
              <Button variant="destructive" size="sm" onClick={() => { setActionType('reject'); setIsActionDialogOpen(true); }}>
                <XCircle className="w-4 h-4 mr-1.5" />
                Reject
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Quiz creation loading dialog */}
      <Dialog open={isCreatingQuiz} onOpenChange={() => {}}>
        <DialogContent
          className="sm:max-w-md"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Create Quiz</DialogTitle>
            <DialogDescription>AI is generating your quiz questions. This may take a moment…</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                <Loader2 className="h-5 w-5 text-primary animate-spin" />
              </div>
              <div className="flex-1 space-y-2">
                <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
                  <div className="h-full w-1/3 animate-pulse rounded-full bg-primary" />
                </div>
                <p className="text-sm text-muted-foreground">Analyzing policy and generating quiz questions…</p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Merged cover + header card */}
      <Card className="shadow-card overflow-hidden">
        {/* Cover — full-width, flush to card edges */}
        <PolicyCover
          coverUrl={company?.cover_image_url}
          companyName={company?.name ?? ''}
          className="w-full h-40"
        />

        {/* Title + metadata — immediately below the cover, no gap */}
        <CardContent className="pt-4 pb-5">
          <div className="flex items-start gap-4">
            <img src={logo} alt="Company Logo" className="w-10 h-10 object-contain shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4">
                <h1 className="text-2xl font-semibold text-foreground leading-tight">{policy.title}</h1>
                <StatusBadge status={policy.status} className="shrink-0">
                  {policy.status === 'approved' && <CheckCircle className="w-3 h-3 mr-1" />}
                  {policy.status === 'under_review' && <Clock className="w-3 h-3 mr-1" />}
                  {getStatusDisplay(policy.status)}
                </StatusBadge>
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <FileText className="w-3.5 h-3.5" />
                  {getDepartmentDisplay(policy.department || 'General')}
                </span>
                <span className="text-border">·</span>
                <span>v{policy.version}</span>
                <span className="text-border">·</span>
                <span>Created {format(new Date(policy.created_at), "MMM d, yyyy")}</span>
                <span className="text-border">·</span>
                <span>Effective: {policy.effective_date ? format(new Date(policy.effective_date), "MMM d, yyyy") : '—'}</span>
                <span className="text-border">·</span>
                <span>Renewal: {policy.next_renewal_date ? format(new Date(policy.next_renewal_date), "MMM d, yyyy") : '—'}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {(policy.program || linkedControls.length > 0) && (
        <Card className="shadow-card">
          <CardContent className="p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-3">
                <div className="rounded-md bg-primary/10 p-2 text-primary">
                  <ShieldCheck className="h-4 w-4" />
                </div>
                <div>
                  <p className="font-medium">{policy.program ? "Compliance program" : "Control coverage"}</p>
                  <p className="text-sm text-muted-foreground">
                    {policy.program
                      ? `${formatFramework(policy.program.framework)} · ${policy.program.name}`
                      : "This policy is linked to framework controls in an active compliance program."}
                  </p>
                </div>
              </div>
              <div className="flex max-w-full flex-wrap gap-2 sm:justify-end">
                {linkedControls.length > 0 ? (
                  <>
                    {linkedControls.slice(0, 8).map((mapping) => (
                      <Badge key={mapping.id} variant="outline">
                        {mapping.framework_control?.framework} {mapping.framework_control?.control_id}
                      </Badge>
                    ))}
                    {linkedControls.length > 8 && <Badge variant="secondary">+{linkedControls.length - 8}</Badge>}
                  </>
                ) : (
                  <Badge variant="outline">Program linked</Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Policy Details and Content */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-10 gap-6">
        <div className="md:col-span-2 lg:col-span-6 space-y-6">
          {/* Executive Summary - Only for approved policies */}
          {policy.status === 'approved' && (
            <ExecutiveSummary
              policyId={policy.id}
              policyTitle={policy.title}
              policyContent={policy.content}
              policyCreatedBy={policy.created_by}
              isPolicyCreator={profile?.id === policy.created_by}
            />
          )}

          {/* Policy Content */}
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>Policy Content</CardTitle>
            </CardHeader>
            <CardContent>
              <CommentAnnotator
                content={policy.content}
                onSelectForComment={(sel) => {
                  setPendingSelection(sel);
                  setSidebarTab('comments');
                }}
              />
            </CardContent>
          </Card>

          {/* Compliance Checklist - Only show for approved policies */}
          {policy.status === 'approved' && (
            <ComplianceChecklist
              policyId={policy.id}
              policyTitle={policy.title}
              policyContent={policy.content}
              policyCreatedBy={policy.created_by}
              isPolicyCreator={profile?.id === policy.created_by}
            />
          )}

          {policy.status === 'approved' && (
            <Card className="shadow-card">
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <CardTitle className="flex items-center gap-2">
                    <ListChecks className="h-5 w-5" />
                    Post-Approval Tasks
                  </CardTitle>
                  {(canEditPolicy || isAdmin) && (
                    <Button size="sm" onClick={() => setIsTaskModalOpen(true)}>
                      <Plus className="mr-1.5 h-4 w-4" />
                      Add Task
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {linkedTasks.length > 0 ? (
                  linkedTasks.map((task) => (
                    <Link
                      key={task.id}
                      to={`/compliance/tasks/${task.id}`}
                      className="flex flex-col gap-2 rounded-md border p-3 transition-colors hover:bg-muted/40 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0">
                        <p className="font-medium">{task.title}</p>
                        <p className="text-sm text-muted-foreground">
                          {task.program?.framework ? `${formatFramework(task.program.framework)} · ` : ""}
                          {task.program_phase ? task.program_phase.replace("_", " ") : "Policy task"}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline">{task.priority.toUpperCase()}</Badge>
                        <Badge variant={task.status === "complete" ? "default" : "secondary"}>{task.status.replace("_", " ")}</Badge>
                      </div>
                    </Link>
                  ))
                ) : (
                  <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                    No follow-up tasks are linked yet. Add tasks for distribution, acknowledgement, training, evidence collection, or recurring review.
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar - tabbed Workflow / Comments */}
        <div className="md:col-span-1 lg:col-span-4 min-w-0">
          <Tabs value={sidebarTab} onValueChange={setSidebarTab} className="w-full">
            <TabsList className="w-full mb-4">
              <TabsTrigger value="workflow" className="flex-1">Workflow</TabsTrigger>
              <TabsTrigger value="comments" className="flex-1">Comments</TabsTrigger>
            </TabsList>

            <TabsContent value="workflow" className="space-y-4 mt-0">
              <Card className="shadow-card">
                <CardContent className="pt-4">
                  <WorkflowVisualization
                    currentStatus={policy.status}
                    creator={policy.creator}
                    reviewers={reviewers}
                    approvers={approvers}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="comments" className="space-y-4 mt-0">
              <PolicyComments
                policyId={policy.id}
                assignedReviewerIds={reviewers.map((reviewer) => reviewer.id)}
                assignedApproverIds={approvers.map((approver) => approver.id)}
                canBackToRework={['under_review', 'pending_approval'].includes(policy.status) && (isAssignedReviewer || isAssignedApprover || isAdmin)}
                onBackToRework={handleBackToReworkFromComment}
                pendingSelection={pendingSelection}
                onClearPendingSelection={() => setPendingSelection(null)}
              />
              {(policy.status === 'under_review' || policy.status === 'pending_approval') && (
                <ApprovalConditions
                  policyId={policy.id}
                  policyCreatedBy={policy.created_by}
                  assignedApprovers={approvers.map(a => a.id)}
                />
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Audit Trail - collapsible */}
      <Collapsible open={isAuditTrailOpen} onOpenChange={setIsAuditTrailOpen}>
        <CollapsibleTrigger asChild>
          <button className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors w-full text-left py-2">
            <ChevronDown className={`w-4 h-4 transition-transform ${isAuditTrailOpen ? 'rotate-180' : ''}`} />
            Audit Trail
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <PolicyActivityLog
            policyId={policy.id}
            policyTitle={policy.title}
          />
        </CollapsibleContent>
      </Collapsible>

      {/* Policy Preview Dialog */}
      {policy && (
        <PolicyPreview
          policy={policy}
          activityLog={activityLog}
          isOpen={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
        />
      )}

      {policy && (
        <TaskFormModal
          open={isTaskModalOpen}
          onOpenChange={setIsTaskModalOpen}
          defaultTaskData={{
            policy_id: policy.id,
            program_id: policy.program_id,
            program_phase: policy.program_id ? "policies" : null,
            reference_type: policy.program_id ? "program" : "policy",
            title: `Follow up on ${policy.title}`,
          }}
          onSave={handleSavePolicyTask}
        />
      )}

      {/* Danger Zone */}
      {(canEditPolicy || hasAnyRole(['creator', 'admin'])) && (
        <div className="flex items-center justify-between border-t pt-4">
          <p className="text-xs text-muted-foreground">Permanently delete this policy and all associated data.</p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="sm" disabled={deleting} className="text-destructive hover:text-destructive hover:bg-destructive/10">
                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                {deleting ? 'Deleting…' : 'Delete Policy'}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Policy</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete "{policy.title}"? This action cannot be undone and will permanently remove the policy and all associated workflow data.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeletePolicy}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete Policy
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      {/* Workflow Action Dialog */}
      <Dialog open={isActionDialogOpen} onOpenChange={setIsActionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'submit' && 'Submit for Review'}
              {actionType === 'send_approval' && 'Send for Approval'}
              {actionType === 'approve' && 'Approve Policy'}
              {actionType === 'reject' && 'Reject Policy'}
              {actionType === 'rework' && 'Back to Rework'}
            </DialogTitle>
            <DialogDescription>
              {actionType === 'submit' && 'Add any comments before submitting this policy for review (optional).'}
              {actionType === 'send_approval' && 'Add any comments before sending this policy for approval (optional).'}
              {actionType === 'approve' && 'Add any comments before approving this policy (optional).'}
              {actionType === 'reject' && 'Please provide a reason for rejecting this policy (required).'}
              {actionType === 'rework' && 'Explain what needs to be reworked before this policy can proceed.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">
                Comments {actionType === 'reject' && '(Required)'}
              </label>
              <Textarea
                value={actionComments}
                onChange={(e) => setActionComments(e.target.value)}
                placeholder={actionType === 'reject' 
                  ? 'Enter the reason for rejection...' 
                  : actionType === 'rework'
                  ? 'Describe the revisions required...'
                  : actionType === 'submit'
                  ? 'Add any comments...'
                  : 'Add any comments...'}
                rows={4}
                className="mt-2"
                required={actionType === 'reject'}
              />
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setIsActionDialogOpen(false);
                setActionComments('');
                setActionType(null);
              }}>
                Cancel
              </Button>
              <Button 
                onClick={handleWorkflowAction}
                variant={actionType === 'reject' ? 'destructive' : 'default'}
              >
                {actionType === 'submit' && 'Submit for Review'}
                {actionType === 'send_approval' && 'Send for Approval'}
                {actionType === 'approve' && 'Approve Policy'}
                {actionType === 'reject' && 'Reject Policy'}
                {actionType === 'rework' && 'Send Back to Rework'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reopen as Draft Confirmation */}
      <Dialog open={isReopenDialogOpen} onOpenChange={setIsReopenDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Approved Policy</DialogTitle>
            <DialogDescription>
              Editing an approved policy will move it back to Drafts. It must go through review and approval again. While in draft, it will be removed from the AI Agent's indexing and will not appear in answers. Do you want to continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsReopenDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleReopenAsDraft}>Move to Drafts and Edit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Template Modal */}
      <CreateTemplateModal
        open={isCreateTemplateOpen}
        onOpenChange={setIsCreateTemplateOpen}
        onSubmit={handleCreateTemplate}
        initialData={{
          title: policy?.title || "",
          department: policy?.department || "",
        }}
      />
    </div>
  );
};

export default PolicyView;
